import { client } from '../../services/api';
import type { PatientMatchResult } from './types';

/** Demographics used to look a patient up in Kinnser/WellSky via the RogerDoc match API. */
export interface KinnserMatchDemographics {
  firstName: string;
  middleName?: string | null;
  lastName: string;
  mrn?: string | null;
  dob?: string | null;
  ssn?: string | null;
  address?: string | null;
  zipCode?: string | null;
  /** Intake-stored gender ('M' / 'F'); mapped to the RogerDoc code by mapMatchGender. */
  gender?: string | null;
  email?: string | null;
  city?: string | null;
  state?: string | null;
}

/** Map an intake gender ('M' / 'F') to the RogerDoc match code ('1' / '2'). */
export function mapMatchGender(gender: string | null | undefined): string {
  if (!gender) return '';
  const normalized = gender.toUpperCase();
  if (normalized === 'M') return '1';
  if (normalized === 'F') return '2';
  return '';
}

/** Request body for the RogerDoc/Kinnser patient match endpoint. */
export function buildKinnserMatchRequestBody(d: KinnserMatchDemographics) {
  return {
    first_name: d.firstName.trim(),
    middle_name: d.middleName?.trim().charAt(0).toUpperCase() || '',
    last_name: d.lastName.trim(),
    mrn: d.mrn?.trim() || '',
    date_of_birth: d.dob?.trim() || '',
    ssn: d.ssn?.trim() || '',
    address: d.address?.trim() || '',
    zip_code: d.zipCode?.trim() || '',
    gender: mapMatchGender(d.gender),
    email_address: d.email?.trim() || '',
    city: d.city?.trim() || '',
    state: d.state?.trim() || '',
  };
}

/**
 * POST the RogerDoc/Kinnser patient match (with episode info) and return the rows
 * tagged with `match_source: 'kinnser'`. Used by the extraction-tab duplicate check
 * and by the scheduling-tab "Refresh from Kinnser" action.
 */
export async function fetchKinnserMatches(
  clinicKey: string,
  demographics: KinnserMatchDemographics,
  signal?: AbortSignal
): Promise<PatientMatchResult[]> {
  const res = await client.request({
    method: 'POST',
    url: `/rogerdoc/api/patient/match/${clinicKey}`,
    query: { include_episode_info: 'true' },
    body: buildKinnserMatchRequestBody(demographics),
    signal,
  });
  const rows = (res.data as PatientMatchResult[]) ?? [];
  return rows.map(r => ({ ...r, match_source: 'kinnser' as const }));
}
